import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button,  Paper, Snackbar, Typography, IconButton } from '@mui/material';
import { Alert } from "@mui/lab";
import { AgGridReact } from "ag-grid-react";
import type {
	ColDef,
	DefaultMenuItem,
	EditableCallbackParams,
	GetContextMenuItemsParams,
	GridApi,
	ICellRendererParams,
	MenuItemDef,
	SelectionChangedEvent,
	RowNode,
	ProcessCellForExportParams,
} from "ag-grid-community";
import FormatListBulletedSharpIcon from "@mui/icons-material/FormatListBulletedSharp";
import AddBoxIcon from "@mui/icons-material/AddBox";
import ScheduleIcon from "@mui/icons-material/Schedule";
import Tooltip from "@mui/material/Tooltip";
import {
	dxtToLocalServerTime,
	ErrorModal,
	AssetTreePicker,
	useUserContext,
	useToken,
	getMomentTz,
} from "@dexteel/mesf-core";
import { DeleteWorkOrderModal } from "@/shared/work-orders-modals/components/modal-delete-work-order";
import { EditWorkOrderModal } from "@/shared/work-orders-modals/components/modal-edit-work-order";
import { OpenWorkOrderModal } from "@/shared/work-orders-modals/components/modal-open-work-order";
import { CloseWorkOrderModalWithAttachments } from "@/shared/work-orders-modals/components/modal-close-work-order";
import { INITIAL_VALUES_MODAL } from "@/shared/work-orders-modals/models/ModalInitialValue";
import { AssetNameColumnColumn } from "./components/assetName-column";
import type { WorkOrder } from "./models/WorkOrder";
import type { ModalWorkOrder } from "@/shared/work-orders-modals/models/ModalWorkOrder";
import type { TempWorkOrder } from "./models/TempWorkOrder";
import { CloseGroupWorkOrdersDialog } from "./components/close-group-wo-dialog/close-group-wo-dialog";
import { WorkOrdersCommentsColumn } from "./components/comments-buttons/comments-buttons.columns";
import { WorkOrdersResourcesColumn } from "./components/resources-buttons/resources-buttons.columns";
import { ExcelButton } from "@/components/buttons/excel-button";
import { openWorkOrderPdf } from "@/shared/work-order-pdf";
import { useWorkOrdersContext } from "./context/workOrdersContext";
import { TableWorkOrdesResoures } from "@/shared/work-orders-modals/components/work-order-resources/table-work-ordes-resoures";
import { Filters } from "./filters";
import { useInitializeWorkOrders } from "./hooks/useInitializeWorkOrders";
import { useLoadfilters } from "./hooks/useLoadFilters";
import { useQueryClient } from "@tanstack/react-query";
import { WORK_ORDERS_QUERY_KEYS, useWorkOrders } from "./hooks/useWorkOrdersQueries";
import CustomLoadingOverlay from "../../components/loaders/LazyLoading/LazyLoading";
import { DeleteMultipleWorkOrdersModal } from "@/shared/work-orders-modals/components/modal-delete-multiple-work-orders";
import { globalDefaultColDef } from "@setup/ag-grid/mesf-theme";
import Grid2 from '@mui/material/Grid2';
import { getCraftLetter } from "@/utils/craftLetter";

export default function WorkOrders() {
	const {
		state: { defaultAreaId },
	} = useUserContext();
	const { getUserName } = useToken();
	const moment = getMomentTz();
	const {
		state: {
			dataToGetWorkOrders,
			dataToGetWorkOrders: { AssetId, assetSearchName },
			listWorkOrderSelected,
			showDialogToCloseGroup,
		},
		actions: {
			setListWorkOrderSelected,
			setFirstTime,
			setShowDialogToCloseGroup,
			setAssetIdWorkOrders,
		},
	} = useWorkOrdersContext();

	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [isCraftLoading, setIsCraftLoading] = useState<boolean>(false);
	const [craftError, setCraftError] = useState<string>("");
	const { loadFilterCrafts, loadFilterCloser } = useLoadfilters({
		setIsCraftLoading,
		setCraftError,
	});
	const [error, setError] = useState<string>("");
	const [noResults, setNoResults] = useState<boolean>(false);

	const { workOrders, isLoading, error: listError } = useWorkOrders(
		dataToGetWorkOrders,
		AssetId !== null && AssetId !== undefined,
	);
	const queryClient = useQueryClient();
	const refreshWorkOrders = useCallback(
		() =>
			queryClient.invalidateQueries({ queryKey: WORK_ORDERS_QUERY_KEYS.all }),
		[queryClient],
	);

	useEffect(() => {
		if (listError) setError(listError.message);
	}, [listError]);

	useEffect(() => {
		if (!isLoading && AssetId !== null && AssetId !== undefined) {
			setNoResults(workOrders.length === 0);
		}
	}, [workOrders, isLoading, AssetId]);

	const customExcelExport = useCallback((onlySelected = false) => {
		if (!gridApi) return;

		const params = {
			fileName: onlySelected ? "WorkOrders_Selected.xlsx" : "WorkOrders_Export.xlsx",
			onlySelected,
			columnKeys: [
				"WorkOrderName",
				"AssetName",
				"Title",
				"OpenTimestamp",
				"CloseTimestamp",
				"ClosedBy",
				"TypeCode",
				"CraftName",
				"DownHours",
				"Comments",
			],
			processCellCallback: (params: ProcessCellForExportParams) => {
				// AG-Grid export callback - simplified to just return value
				return params.value || "";
			},
		};

		gridApi.exportDataAsExcel(params);
	}, [gridApi]);
	const [searchParams, setSearchParams] = useSearchParams();

	const [selectedRow, setSelectedRow] = useState(INITIAL_VALUES_MODAL);
	const [selectedRows, setSelectedRows] = useState<WorkOrder[]>([]);

	const [columnDefs, setColumnDefs] = useState<ColDef[]>([]);

	const navigate = useNavigate();
	const gridRef = useRef<AgGridReact>(null);

	const [openWorkOrderEditModal, setOpenWorkOrderEditModal] =
		useState<boolean>(false);
	const [openCloseWorkOrderModal, setOpenCloseWorkOrderModal] =
		useState<boolean>(false);
	const [openWorkOrderModal, setOpenWorkOrderModal] = useState<boolean>(false);
	const [openDeleteWorkOrderModal, setOpenDeleteWorkOrderModal] =
		useState<boolean>(false);
	const [
		openDeleteMultipleWorkOrdersModal,
		setOpenDeleteMultipleWorkOrdersModal,
	] = useState<boolean>(false);
	const [openResourcesModal, setOpenResourcesModal] = useState<boolean>(false);
	const [newMode, setNewMode] = useState<boolean>(false);
	const [exportExcel, setExportExcel] = useState<boolean>(false);

	const handleClose = (event: Event | React.SyntheticEvent, reason?: string) => {
		if (reason === "clickaway") {
			return;
		}
		setNoResults(false);
	};

	const handleResourcesClick = (rowData: WorkOrder) => {
		setSelectedRow(rowData as ModalWorkOrder);
		setOpenResourcesModal(true);
	};

	const onMenuOptionSelected = (
		option:
			| "NEW"
			| "EDIT"
			| "EDIT-COMMENTS"
			| "CLOSE-ORDER"
			| "OPEN-ORDER"
			| "DELETE-ORDER"
			| "DELETE-MULTIPLE-ORDER",
		row: WorkOrder | ModalWorkOrder | RowNode<WorkOrder>[] | TempWorkOrder,
	) => {
		if (option !== "DELETE-MULTIPLE-ORDER" && option !== "NEW") {
			setSelectedRow(row as ModalWorkOrder);
		} else if (option === "DELETE-MULTIPLE-ORDER") {
			setSelectedRows((row as RowNode<WorkOrder>[]).map((node) => node.data).filter((data): data is WorkOrder => data !== undefined));
		} else if (option === "NEW") {
			setNewMode(true);
			setSelectedRow(row as ModalWorkOrder);
		}

		switch (option) {
			case "NEW":
			case "EDIT":
			case "EDIT-COMMENTS":
				setOpenWorkOrderEditModal(true);
				break;
			case "CLOSE-ORDER":
				setOpenCloseWorkOrderModal(true);
				break;
			case "OPEN-ORDER":
				setOpenWorkOrderModal(true);
				break;
			case "DELETE-ORDER":
				setOpenDeleteWorkOrderModal(true);
				break;
			case "DELETE-MULTIPLE-ORDER":
				setOpenDeleteMultipleWorkOrdersModal(true);
				break;
		}
	};

	const onActionsClick = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		params: EditableCallbackParams,
	) => {
		params.api.showContextMenu({
			column: params.column,
			rowNode: params.node,
			value: null,
			source: 'ui',
		});
	};

	const renderCategoryCell = (props: ICellRendererParams) => {
		return (
			<div
				style={{
					height: 32,
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
				}}>
				<div
					style={{
						height: 26,
						width: 26,
						borderRadius: 4,
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						background: props.data?.CraftColor || "#FFFFFF",
					}}>
					<div style={{ color: "#FFF", fontWeight: 600 }}>
						<div>{getCraftLetter(props.data?.CraftName, props.data?.CraftCode)}</div>
					</div>
				</div>
			</div>
		);
	};

	const renderTypeCell = (props: ICellRendererParams) => {
		return (
			<div
				style={{
					height: 32,
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
				}}>
				<div
					style={{
						height: 26,
						width: 26,
						borderRadius: 4,
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						background: props.data?.TypeCode == "PM" ? "#00008B" : "#D3D3D3",
					}}>
					<div
						style={{
							color: props.data?.TypeCode == "PM" ? "#FFFFFF" : "#000000",
							fontWeight: 600,
						}}>
						<div>{props.data?.TypeCode || ""}</div>
					</div>
				</div>
			</div>
		);
	};

	const onSelectionChanged = useCallback((event: SelectionChangedEvent) => {
		const listOfWorkOrders = event.api.getSelectedNodes();
		setListWorkOrderSelected(listOfWorkOrders);
	}, []);

	const loadingOverlayComponent = useMemo(() => {
		return CustomLoadingOverlay;
	}, []);


	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			// If clicking on empty space (no data in node)
			if (!params.node?.data) {
				const targetAssetId = AssetId ?? defaultAreaId ?? 0;
				const tempWorkOrder = {
					AssetId: targetAssetId,
					AssetName: assetSearchName ?? "",
					DownHours: 0,
					WorkOrderId: null,
					Title: "",
					CraftId: 1,
					TypeCode: "RM",
					WorkOrderStatusCode: "O",
					WorkOrderName: "",
					WorkOrderNumberOfWorkers: null,
					OpenTimestamp: moment(new Date()).format("MM/DD/YYYY HH:mm"),
					WorkOrderPlannedHours: null,
					WorkOrderOpenedBy: getUserName(),
				};
				return [
					{
						name: "Add Work Order",
						action: () => {
							onMenuOptionSelected("NEW", tempWorkOrder);
						},
						icon: '<i class="fas fa-plus"></i>',
					},
				];
			}

			const selectedNodes = params.api.getSelectedNodes();
			const isMultipleSelection = selectedNodes.length> 1;
			const status = params.node?.data.WorkOrderStatusCode;
			const result: (DefaultMenuItem | MenuItemDef)[] = [];

			// For multiple selections: only show relevant operations
			if (isMultipleSelection) {
				// Get all selected statuses to determine Close/Open behavior
				const selectedStatuses = selectedNodes.map(node => node.data?.WorkOrderStatusCode);
				const hasOpenOrders = selectedStatuses.includes("O");
				const hasClosedOrders = selectedStatuses.includes("C");
				const hasMixedStatuses = hasOpenOrders && hasClosedOrders;

				// Close Orders option (only if there are open orders)
				if (hasOpenOrders) {
					result.push({
						name: "Close Orders",
						action: () => {
							setShowDialogToCloseGroup(true);
						},
						icon: '<i class="far fa-times-circle"></i>',
						disabled: hasMixedStatuses,
					});
				}

				// TODO: Re-open Work Orders - Hidden until client request or further adjustments
				// Open Orders option (only if there are closed orders)
				// if (hasClosedOrders) {
				// 	result.push({
				// 		name: "Open Orders",
				// 		action: () => {
				// 			// TODO: Implement open multiple orders functionality
				// 			console.log("Open multiple orders not implemented yet");
				// 		},
				// 		icon: '<i class="far fa-plus-square"></i>',
				// 		disabled: hasMixedStatuses,
				// 	});
				// }

				// Delete Orders - always available for multiple selection
				result.push({
					name: "Delete Orders",
					action: () => {
						onMenuOptionSelected(
							"DELETE-MULTIPLE-ORDER",
							selectedNodes as RowNode<WorkOrder>[],
						);
					},
					icon: '<i class="fas fa-trash"></i>',
				});

				// Export / print actions for the selected orders
				result.push("separator");
				result.push({
					name: "Export Selected to Excel",
					action: () => customExcelExport(true),
					icon: '<i class="fas fa-file-excel"></i>',
				});
				result.push({
					name: "Generate Work Orders PDF",
					action: () =>
						openWorkOrderPdf(selectedNodes.map((n) => n.data?.WorkOrderId)),
					icon: '<i class="fas fa-file-pdf"></i>',
				});
			} else {
				// Single selection: show all options
				result.push({
					name: "Edit",
					action: () => {
						onMenuOptionSelected("EDIT-COMMENTS", params.node?.data);
					},
					icon: '<i class="fas fa-edit"></i>',
				});

				// Resources option
				result.push({
					name: "Resources",
					action: () => {
						handleResourcesClick(params.node?.data);
					},
					icon: '<i class="fas fa-folder"></i>',
				});

				// Add "New Work Order" option
				const targetAssetId = AssetId ?? defaultAreaId ?? 1;
				const tempWorkOrder = {
					AssetId: targetAssetId,
					AssetName: assetSearchName ?? "",
					DownHours: 0,
					WorkOrderId: null,
					Title: "",
					CraftId: 1,
					TypeCode: "RM",
					WorkOrderStatusCode: "O",
					WorkOrderName: "",
					WorkOrderNumberOfWorkers: null,
					OpenTimestamp: moment(new Date()).format("MM/DD/YYYY HH:mm"),
					WorkOrderPlannedHours: null,
					WorkOrderOpenedBy: getUserName(),
				};
				result.push({
					name: "New Work Order",
					action: () => {
						onMenuOptionSelected("NEW", tempWorkOrder);
					},
					icon: '<i class="fas fa-plus"></i>',
				});

				// Close/Open based on current status
				if (status === "O") {
					result.push({
						name: "Close Work Order",
						action: () => {
							onMenuOptionSelected("CLOSE-ORDER", params.node?.data);
						},
						icon: '<i class="far fa-times-circle"></i>',
					});
				}

				// TODO: Re-open Work Order - Hidden until client request or further adjustments
				// if (status === "C") {
				// 	result.push({
				// 		name: "Open Work Order",
				// 		action: () => {
				// 			onMenuOptionSelected("OPEN-ORDER", params.node?.data);
				// 		},
				// 		icon: '<i class="far fa-plus-square"></i>',
				// 	});
				// }

				// Delete single work order
				result.push({
					name: "Delete Work Order",
					action: () => {
						onMenuOptionSelected("DELETE-ORDER", params.node?.data);
					},
					icon: '<i class="fas fa-trash"></i>',
				});

				// Generate a printable PDF of this order (details + comments)
				result.push("separator");
				result.push({
					name: "Generate Work Order PDF",
					action: () => openWorkOrderPdf([params.node?.data?.WorkOrderId]),
					icon: '<i class="fas fa-file-pdf"></i>',
				});
			}

			return result;
		},
		[listWorkOrderSelected, AssetId, assetSearchName, defaultAreaId, getUserName, customExcelExport],
	);

	const onDobleClicked = () => {
		const selectedRow1 = gridRef.current!.api.getSelectedNodes();
		const row = selectedRow1[0]?.data;
		if (!row) return;
		// Open work order: double-click jumps straight to the close action.
		// Closed work order: double-click keeps opening the edit/details modal.
		if (row.WorkOrderStatusCode === "O") {
			onMenuOptionSelected("CLOSE-ORDER", row);
		} else {
			onMenuOptionSelected("EDIT", row);
		}
	};


	const changeAsset = (value: number | null) => {
		navigate(`/work-orders/${value}`, { replace: true });
	};

	useInitializeWorkOrders();

	useEffect(loadFilterCloser, []);

	useEffect(() => {
		loadFilterCrafts();
	}, []);

	useEffect(() => {
		if (exportExcel) {
			customExcelExport();
			setExportExcel(false);
		}
	}, [exportExcel, customExcelExport]);

	useEffect(() => {
		setColumnDefs([
			{
				colId: "checkbox",
				flex: 1,
				maxWidth: 33,
				filter: false,
				checkboxSelection: true,
				headerCheckboxSelection: true,
			},
			{
				field: "WorkOrderName",
				headerName: "Name",
				minWidth: 100,
				flex: 1,
				headerClass: "ag-center-header",
			},
			{
				field: "AssetName",
				headerName: "Asset",
				cellRenderer: AssetNameColumnColumn,
				minWidth: 150,
				flex: 2,
			},
			{ field: "Title", headerName: "Title", minWidth: 170, flex: 3 },
			{
				field: "OpenTimestamp",
				headerName: "Open",
				minWidth: 110,
				maxWidth: 110,

				flex: 1,
				valueGetter: (params) =>
					dxtToLocalServerTime(params.data.OpenTimestamp, "MM/dd/yy HH:mm"),
			},
			{
				field: "CloseTimestamp",
				headerName: "Closed",
				minWidth: 110,
				maxWidth: 110,
				flex: 1,
				valueGetter: (params) =>
					dxtToLocalServerTime(params.data.CloseTimestamp, "MM/dd/yy HH:mm"),
			},
			{
				field: "ClosedBy",
				headerName: "Closed By",
				minWidth: 110,
				flex: 1,
				headerClass: "ag-center-header",
			},
			{
				field: "TypeCode",
				headerName: "Type",
				minWidth: 40,
				maxWidth: 40,
				flex: 1,
				cellRenderer: renderTypeCell,
			},
			{
				field: "CraftName",
				headerName: "Craft",
				minWidth: 40,
				maxWidth: 40,
				flex: 1,
				cellRenderer: renderCategoryCell,
			},
			{
				field: "DownHours",
				headerName: "Down Hours",
				minWidth: 80,
				maxWidth: 80,
				flex: 1,
			},
			{
				field: "DownDayCode",
				headerName: "Down Day",
				minWidth: 60,
				maxWidth: 60,
				flex: 1,
				cellRenderer: (params: ICellRendererParams) =>
					params.value ? (
						<Tooltip
							title={params.value === "N" ? "Next down day" : "Down day"}
						>
							<span
								style={{
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									width: "100%",
									height: "100%",
								}}
							>
								<ScheduleIcon
									fontSize="small"
									sx={{ color: params.value === "N" ? "error.main" : "#1976d2" }}
								/>
							</span>
						</Tooltip>
					) : (
						""
					),
			},
			{
				field: "Comments",
				headerName: "Comments",
				hide: true,
			},
			{
				field: "AssetName",
				headerName: "Asset Name",
				hide: true,
			},
			{
				headerName: "Resources",
				flex: 1,
				minWidth: 80,
				maxWidth: 80,
				cellClass: "ag-columns",
				cellRenderer: (params: EditableCallbackParams) => {
					return (
						<WorkOrdersResourcesColumn
							params={params}
							onResourcesClick={handleResourcesClick} />
					);
				},
			},
			{
				headerName: "",
				flex: 1,
				minWidth: 220,
				cellClass: "ag-columns",
				wrapText: false,
				autoHeight: false,
				cellRenderer: (params: EditableCallbackParams) => {
					return (
						<Grid2 container
							style={{
								height: "100%",
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
							}}>
							<Grid2 size={{ xs: 4, md: 4 }} style={{ display: "flex", justifyContent: "center", alignItems: "center", }}>
								<WorkOrdersCommentsColumn
									searchData={refreshWorkOrders}
									params={params} />
							</Grid2>
							<Grid2 size={{ xs: 4, md: 4 }} style={{ display: "flex", justifyContent: "center", alignItems: "center", }}>
								<Grid2 spacing={1}
									style={{ justifyContent: "flex-end", minWidth: 30 }}>
									<Grid2 size={{ xs: 12, md: 12 }}>
										<Button
											fullWidth
											style={{}}
											onClick={(e) => onActionsClick(e, params)}>
											<FormatListBulletedSharpIcon
												style={{ height: "auto" }}
												color="action" />
										</Button>
									</Grid2>
								</Grid2>
							</Grid2>
						</Grid2>
					);
				},
			},
		]);
	}, [AssetId]);

	return (
		<>
			<Grid2 container
				style={{
					padding: "10px 20px",
					height: "calc(100vh - 64px)",
					boxSizing: "border-box",
					flexDirection: "column",
					flexWrap: "nowrap",
				}}
				onContextMenu={(e) => e.preventDefault()}>
				<Grid2 container size={{ xs: 12 }} alignItems="center" justifyContent="center" spacing={1}
					style={{ flexBasis: "auto", flexShrink: 0, width: "100%" }}>
					<Grid2 size={{ xs: 12, md: 12 }}>
						<Typography
							variant="h5"
							style={{
								margin: "0",
								fontWeight: 600,
								userSelect: "none",
								marginLeft: 5,
							}}>
							WORK ORDERS
						</Typography>
					</Grid2>

					<Grid2 size={{ xs: 12 }}>
						<Paper elevation={1} style={{ padding: 10, marginBottom: 20 }}>
							<Grid2 container spacing={2} alignItems="center">
								<Grid2 size={{ lg: 2, md: 12, xs: 12 }}>
									<AssetTreePicker
										label="Asset"
										value={Number(AssetId ?? defaultAreaId ?? 0)}
										onChange={changeAsset}
									/>
								</Grid2>

								<Grid2 size={{ lg: 10, md: 12, xs: 12 }}>
									<Filters />
								</Grid2>
							</Grid2>
						</Paper>
					</Grid2>
				</Grid2>

				<Paper
					elevation={1}
					style={{
						width: "100%",
						padding: 10,
						borderRadius: "0.75rem",
						position: "relative",
						flex: 1,
						minHeight: 0,
						boxSizing: "border-box",
						display: "flex",
						flexDirection: "column",
					}}>
					<Grid2 size={{ xs: 12, md: 12 }} style={{ flex: 1, minHeight: 0, width: "100%", paddingBottom: "60px", boxSizing: "border-box" }}>
						<AgGridReact
							ref={gridRef}
							rowData={workOrders}
							columnDefs={columnDefs}
							defaultColDef={globalDefaultColDef}
							rowHeight={33}
							headerHeight={32}
							animateRows={true}
							loadingOverlayComponent={loadingOverlayComponent}
							getContextMenuItems={getContextMenuItems}
							onRowDoubleClicked={onDobleClicked}
							onSelectionChanged={onSelectionChanged}
							rowSelection={"multiple"}
							loading={isLoading}
							onGridReady={(params) => setGridApi(params.api)} />
					</Grid2>
					<IconButton
						onClick={() => {
							const targetAssetId = AssetId ?? defaultAreaId ?? 1;
							const tempWorkOrder = {
								AssetId: targetAssetId,
								AssetName: assetSearchName ?? "",
								DownHours: 0,
								WorkOrderId: null,
								Title: "",
								CraftId: 1,
								TypeCode: "RM",
								WorkOrderStatusCode: "O",
								WorkOrderName: "",
								WorkOrderNumberOfWorkers: null,
								OpenTimestamp: moment(new Date()).format("MM/DD/YYYY HH:mm"),
								WorkOrderPlannedHours: null,
								WorkOrderOpenedBy: getUserName(),
							};
							onMenuOptionSelected("NEW", tempWorkOrder);
						}}
						aria-label="add"
						color="primary"
						style={{
							padding: 0,
							position: "absolute",
							bottom: 15,
							left: 15,
						}}>
						<AddBoxIcon
							style={{ width: 35, height: 35, marginRight: "0" }} />
					</IconButton>
					<div
						style={{
							position: "absolute",
							bottom: 12,
							right: 15,
						}}>
						<ExcelButton onClick={() => setExportExcel(!exportExcel)} />
					</div>
				</Paper>
			</Grid2>
			<Snackbar anchorOrigin={{ vertical: "bottom", horizontal: "center" }} open={noResults} autoHideDuration={2500} onClose={handleClose}>
				<Alert severity="error" onClose={handleClose}>
					No results found
				</Alert>
			</Snackbar>
			<CloseGroupWorkOrdersDialog
				show={showDialogToCloseGroup}
				onHide={(shouldUpdate: boolean) => {
					if (shouldUpdate) {
						refreshWorkOrders();
						setListWorkOrderSelected([]);
					}
					setShowDialogToCloseGroup(false);
				}} />
			<EditWorkOrderModal
				show={openWorkOrderEditModal}
				data={selectedRow}
				initialNewMode={newMode}
				onHide={(shouldUpdate: boolean) => {
					if (shouldUpdate) {
						refreshWorkOrders();
					}
					setNewMode(false);
					setOpenWorkOrderEditModal(false);
				}} />
			<CloseWorkOrderModalWithAttachments
				show={openCloseWorkOrderModal}
				data={selectedRow}
				onHide={(shouldUpdate: boolean) => {
					if (shouldUpdate) {
						refreshWorkOrders();
					}
					setOpenCloseWorkOrderModal(false);
				}} />
			<OpenWorkOrderModal
				show={openWorkOrderModal}
				data={selectedRow}
				onHide={(shouldUpdate: boolean) => {
					if (shouldUpdate) {
						refreshWorkOrders();
					}
					setOpenWorkOrderModal(false);
				}} />
			<DeleteWorkOrderModal
				show={openDeleteWorkOrderModal}
				data={selectedRow}
				onHide={(shouldUpdate: boolean) => {
					if (shouldUpdate) {
						refreshWorkOrders();
					}
					setOpenDeleteWorkOrderModal(false);
				}} />
			<DeleteMultipleWorkOrdersModal
				show={openDeleteMultipleWorkOrdersModal}
				data={selectedRows}
				onHide={(shouldUpdate: boolean) => {
					if (shouldUpdate) {
						refreshWorkOrders();
					}
					setOpenDeleteMultipleWorkOrdersModal(false);
				}} />
			<TableWorkOrdesResoures
				show={openResourcesModal}
				onHide={(shouldUpdate: boolean) => {
					if (shouldUpdate) {
						refreshWorkOrders();
					}
					setOpenResourcesModal(false);
				}}
				AssetId={selectedRow?.AssetId ?? null}
				WorkOrderId={selectedRow?.WorkOrderId ?? null}
				WorkOrderName={selectedRow?.WorkOrderName || ""}
				Title={selectedRow?.Title || ""}
				AssetName={selectedRow?.AssetName || ""}
				TypeCode={selectedRow?.TypeCode || "RM"}
				CraftId={selectedRow?.CraftId ?? undefined}
				OpenTimestamp={selectedRow?.OpenTimestamp ? new Date(selectedRow.OpenTimestamp) : undefined}
				CloseTimestamp={selectedRow?.CloseTimestamp ? new Date(selectedRow.CloseTimestamp) : undefined} />
			<ErrorModal error={error} onHide={() => setError("")} />
			<ErrorModal error={craftError} onHide={() => setCraftError("")} />
		</>
	);
}
